import { HttpError, useOne } from "@refinedev/core";
import { CollapsibleSection } from "components/collapsibleSection";
import Input from "components/input";
import { Label } from "components/label";
import Select from "components/select";
import { SwitchButton } from "components/switchButton";
import { Tag } from "components/tag";
import { ISportParticipantDetail, ISportParticipantForm } from "interfaces";
import { get, isEmpty, isEqual } from "lodash";
import { Controller, useFormContext } from "react-hook-form";
import { Link } from "react-router-dom";
import { getFormattedDateTime } from "utills/helpers";

export const EditInformationSection = ({participantDetailData} : {participantDetailData: ISportParticipantDetail | null}) => {

  const { data: attendantTemplates, isLoading: isFetchingTemplates } = useOne<any[], HttpError>({
    resource: `participant/events/${participantDetailData?.event?.event_id}/attendant-templates/dropdown`,
    id: "",
    meta: {
      resource: "data"
    },
    queryOptions: {
      enabled: !!(participantDetailData?.event?.event_id)
    } as any
  });

  const { data: categoryData, isFetching: isFetchingCategoryData } = useOne<any[], HttpError>({
    resource: `participant/events/${participantDetailData?.event?.event_id}/categories/dropdown`,
    id: "",
    meta: {
      resource: "data"
    },
    queryOptions: {
      enabled: !!(participantDetailData?.event?.event_id)
    } as any
  });

  const {
    register,
    control,
    formState: { errors },
  } = useFormContext<ISportParticipantForm>(); // retrieve all hook methods
  
  const languages = get(participantDetailData, "event.languages", []);

  const attendantTemplateIds = (attendantTemplates?.data ?? []).map(template => ({
    label: template.name,
    value: template.id
  }));

  const categoryIds = ((categoryData?.data) ?? []).map((category: {[x:string]: string;}) => (
    { 
      value: Object.fromEntries(
        languages.map(lang => [`category_name_${lang}`, get(category, `category_${lang}`, "") || ""])
      ),
      label: isEmpty(languages) ? get(category, "category_en", "") : get(category, `category_${languages[0]}`, "")
    }
  ));
  
  return (
    <CollapsibleSection 
      label="Information"
      defaultOpen={true}
      additionalLabel={(
        <Tag text={participantDetailData?.participant.source ?? ""} customClassName="capitalize" type="blue" />
      )}
    >
      <div className="grid gap-x-4 gap-y-6 px-4 py-4 text-sm sm:px-6 grid-cols-12 lg:gap-x-6" style={{wordBreak: "break-word"}}>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-1">
          <div className="col-span-2">
            <p className="font-medium">
              Customer code
            </p>
          </div>
          <div className="col-span-4">
            <p>
              {participantDetailData?.participant.customer_id}
            </p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-3">
          <div className="col-span-2">
            <p className="font-medium">
              Participant ID
            </p>
          </div>
          <div className="col-span-4">
            <p>
              {participantDetailData?.participant.participant_id}
            </p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-5">
          <div className="col-span-2">
            <p className="font-medium">
            Order code
            </p>
          </div>
          <div className="col-span-4">
            <Link to={`/orders/${participantDetailData?.participant.parent_ref_id}`} className="w-fit font-medium text-brand-500 hover:underline">
              {participantDetailData?.participant.parent_ref_id}
            </Link>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-7">
          <div className="col-span-2">
            <Label id="bib" name="BIB" />
          </div>
          <div className="col-span-4">
            <Input
              id="bib"
              type="text"
              placeholder="BIB"
              error={errors["bib"]}
              {...register("bib")}
            />
            {errors["bib"] ? (
              <p className="error-text">
                <span className="font-medium">
                  {errors?.bib.message}
                </span>
              </p>
            ) : null}
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-9">
          <div className="col-span-2">
            <p className="font-medium">
              Full name
            </p>
          </div>
          <div className="col-span-4">
            <p>
              {participantDetailData?.participant.full_name}
            </p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-11">
          <div className="col-span-2">
            <p className="font-medium">
              Participant template name
            </p>
          </div>
          <div className="col-span-4">
            <Controller
              control={control}
              name="attendant_template_id"
              render={({ field: { onChange, value = [], ref } }) => (
                <Select
                  key="attendant_template_id"
                  inputRef={ref}
                  options={attendantTemplateIds}
                  id="attendant_template_id"
                  placeholder="Select Template"
                  defaultValue={
                    attendantTemplateIds.find(
                      (selected: { label: string; value: string }) =>
                        selected.value === value
                    ) ?? ""
                  }
                  value={attendantTemplateIds.find(
                    (selected: { label: string; value: string }) =>
                      selected.value === value
                  )}
                  onChange={(selected: { label: string; value: string }) => {
                    onChange(selected?.value);
                  }}
                  error={errors["attendant_template_id"]}
                  isLoading={isFetchingTemplates}
                />
              )}
            />
            {errors["attendant_template_id"] ? (
              <p className="error-text">
                <span className="font-medium">
                  {errors["attendant_template_id"]?.message}
                </span>
              </p>
            ) : null}
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-[13]">
          <div className="col-span-2">
            <p className="font-medium">
              Update registration info
            </p>
          </div>
          <div className="col-span-4">
            <p>
              {participantDetailData?.participant.is_update_information ? "Yes" : "No"}
            </p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-[15]">
          <div className="col-span-2">
            <p className="font-medium">
              Time to Update registration info
            </p>
          </div>
          <div className="col-span-4">
            <p>
              {participantDetailData?.participant.time_to_update_information}
            </p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-2">
          <div className="col-span-2">
            <p className="font-medium">
            Event Name
            </p>
          </div>
          <div className="col-span-4">
            <p>
              {participantDetailData?.participant.event_name_en}
            </p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-4">
          <div className="col-span-2">
            <p className="font-medium">
              Ticket Name
            </p>
          </div>
          <div className="col-span-4">
            <p>
              {participantDetailData?.participant.ticket_name_en}
            </p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-6">
          <div className="col-span-2">
            <Label id="is_active" name="Status" />
          </div>
          <div className="col-span-4">
            <Controller
              control={control}
              name="is_active"
              render={({ field: { onChange, value = true, ref } }) => (
                <SwitchButton checked={value} onChange={onChange} />
              )}
            />
            {errors["is_active"] ? (
              <p className="error-text">
                <span className="font-medium">
                  {errors?.is_active.message}
                </span>
              </p>
            ) : null}
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-8">
          <div className="col-span-2">
            <p className="font-medium">
              Category
            </p>
          </div>
          <div className="col-span-4">
            <Controller
              control={control}
              name="category"
              render={({ field: { onChange, value = [], ref } }) => (
                <Select
                  key="category"
                  inputRef={ref}
                  options={categoryIds}
                  id="category"
                  placeholder="Select Category"
                  value={categoryIds.find(
                    (selected: { label: string; value: any }) =>
                      isEqual(selected.value, value)
                  )}
                  onChange={(selected: { label: string; value: string }) => {
                    onChange(selected?.value);
                  }}
                  error={errors["category"]}
                  isLoading={isFetchingCategoryData}
                />
              )}
            />
            {errors["category"] ? (
              <p className="error-text">
                <span className="font-medium">
                  {get(errors, "category.message", "") as string}
                </span>
              </p>
            ) : null}
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-10">
          <div className="col-span-2">
            <p className="font-medium">
              Change distance
            </p>
          </div>
          <div className="col-span-4">
            <p className="whitespace-pre-wrap">
              {participantDetailData?.participant.is_change_distance ? "Yes" : "No"}
            </p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-12">
          <div className="col-span-2">
            <p className="font-medium">
              Time to change distance
            </p>
          </div>
          <div className="col-span-4">
            <p className="whitespace-pre-wrap">
              {participantDetailData?.participant.time_to_change_distance}
            </p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-[14]">
          <div className="col-span-2">
            <p className="font-medium">
              Transferred
            </p>
          </div>
          <div className="col-span-4">
            <p className="whitespace-pre-wrap">
              {participantDetailData?.participant.is_transferred ? "Yes" : "No"}
            </p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-[16]">
          <div className="col-span-2">
            <p className="font-medium">
              Time to transfer
            </p>
          </div>
          <div className="col-span-4">
            <p className="whitespace-pre-wrap">
              {participantDetailData?.participant.number_of_times_to_transfer_ticket}
            </p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-[17]">
          <div className="col-span-2">
            <p className="font-medium">
              Created Date
            </p>
          </div>
          <div className="col-span-4">
            <p className="whitespace-pre-wrap">
              {getFormattedDateTime(participantDetailData?.participant.created_at ?? "")}
            </p>
          </div>
        </div>
        {participantDetailData?.participant.reference_code ? (
          <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-[18]">
            <div className="col-span-2">
              <p className="font-medium">
                Reference Code
              </p>
            </div>
            <div className="col-span-4">
              <p className="whitespace-pre-wrap">
                {participantDetailData?.participant.reference_code}
              </p>
            </div>
          </div>
        ) : null}
      </div>
    </CollapsibleSection>   
  )
}
